import * as THREE from "three";
import { Text } from '@react-three/drei';
import React, { memo, useEffect, useRef } from "react";
import { Line, Lines } from "./Line";
import { CatmullRomLine } from '@react-three/drei'
import { ParametricGeometry } from 'three/addons/geometries/ParametricGeometry.js';
import { LoopSubdivision } from 'three-subdivide';

import { drawIntersectionPoints } from "../marker/utils";


/**
 * Calculates the distance between a point and a line defined by two other points.
 * @param {number[]} p1 - The first point of the line, as an array of two numbers [x, y].
 * @param {number[]} p2 - The second point of the line, as an array of two numbers [x, y].
 * @param {number[]} p3 - The point for which the distance to the line is to be calculated, as an array of two numbers [x, y].
 * @returns {number} The distance between the point and the line defined by p1 and p2.
 */
export const distanceFromLine = (p1, p2, p3) => {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  const [x3, y3] = p3;

  const num = Math.abs((y2 - y1) * x3 - (x2 - x1) * y3 + x2 * y1 - y2 * x1);
  const den = Math.sqrt((y2 - y1)**2 + (x2 - x1)**2);

  return num / den;
}

/**
 * Removes outliers from an array based on three points comparison.
 * This function don't include recursive processing based on new points.
 * @param {Array} points - An array of three-dimensional points.
 * @param {number} threshold - The threshold distance for removing outliers.
 * @returns {Array} - An array of points with outliers removed.
 */
export const removeOutliersThreePoints = (points, threshold) => {
  const newPoints = [];
  const len = points.length;

  for (let i = 0; i < len-2; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % len];
    const p3 = points[(i + 2) % len];

    const d = distanceFromLine(p1, p2, p3);

    if (d < threshold) {
      newPoints.push(p2);
    }
  }

  return newPoints;
}



/**
 * Calculates the points of a circle in 3D space.
 *
 * @param {Object} center - The center point of the circle.
 * @param {number} radius - The radius of the circle.
 * @param {number} segments - The number of segments to use when approximating the circle.
 * @returns {Array} An array of THREE.Vector3 points representing the circle.
 */
export const calcCirclePoints = (center, radius, segments) => {
  var pts = [];
  var theta = 360 / segments;

  for(var i=0; i<segments; i++){
    var deg = theta * i * Math.PI / 180;
    var x = center.x + radius * Math.cos(deg);
    var y = center.y;
    var z = center.z + radius * Math.sin(deg);
    var pt = new THREE.Vector3(x, y, z);
    pts.push(pt);
  }
  pts.push(pts[0]); // close

  return pts;
}

/**
 * Smooths a geometry using Loop subdivision.
 *
 * @param {THREE.Geometry} oriGeom - The original geometry to be smoothed.
 * @param {number} iterations - The number of iterations to perform.
 * @returns {THREE.Geometry} The smoothed geometry.
 */
export function smoothGeom(oriGeom, iterations=1){
  const params = {
      split:          true,       // optional, default: true
      uvSmooth:       false,      // optional, default: false
      preserveEdges:  false,      // optional, default: false
      flatOnly:       false,      // optional, default: false
      maxTriangles:   Infinity,   // optional, default: Infinity
  };

  const smoothGeom = LoopSubdivision.modify(oriGeom, iterations, params);

  return smoothGeom;
}



/** Get cube base points
 * @param {Vector3} point
 * @returns {Vector3[]} bottom points
 */
export function getBottomCubePoints(bottomCenter) {
  // bottom curve
  const bottomWidth = 25;
  var filletBottom = 1;
  const divFillet = 5;

  var bottomPoints = []
  const centerFillet1 = new THREE.Vector2(bottomWidth/2-filletBottom, bottomWidth/2-filletBottom);
  const centerFillet2 = new THREE.Vector2(-bottomWidth/2+filletBottom, bottomWidth/2-filletBottom);
  const centerFillet3 = new THREE.Vector2(-bottomWidth/2+filletBottom, -bottomWidth/2+filletBottom);
  const centerFillet4 = new THREE.Vector2(bottomWidth/2-filletBottom,  -bottomWidth/2+filletBottom);

  const fillet1 = new THREE.EllipseCurve(
    centerFillet1.x, centerFillet1.y,
    filletBottom, filletBottom,
    0, 0.5 * Math.PI,
    false, 0
  );
  const fillet2 = new THREE.EllipseCurve(
    centerFillet2.x, centerFillet2.y,
    filletBottom, filletBottom,
    0.5 * Math.PI, Math.PI,
    false, 0
  );
  const fillet3 = new THREE.EllipseCurve(
    centerFillet3.x, centerFillet3.y,
    filletBottom, filletBottom,
    Math.PI, 1.5 * Math.PI,
    false, 0
  );
  const fillet4 = new THREE.EllipseCurve(
    centerFillet4.x, centerFillet4.y,
    filletBottom, filletBottom,
    1.5 * Math.PI, 2 * Math.PI,
    false, 0
  );

  const points1 = fillet1.getPoints( divFillet );
  const points2 = fillet2.getPoints( divFillet );
  const points3 = fillet3.getPoints( divFillet );
  const points4 = fillet4.getPoints( divFillet );

  bottomPoints = bottomPoints.concat(points1);
  bottomPoints = bottomPoints.concat(points2);
  bottomPoints = bottomPoints.concat(points3);
  bottomPoints = bottomPoints.concat(points4);
  bottomPoints.push(bottomPoints[0]);

  bottomPoints = bottomPoints.map((point) => {
     return new THREE.Vector3(point.x+bottomCenter.x, bottomCenter.y, point.y+bottomCenter.z)
  });

  const bottomPointsAll = [];
  bottomPointsAll.push(bottomPoints);
  // for (let i = 1; i < 10; i++) {
  //   bottomPointsAll.push(bottomPoints.map((point) => {
  //     return new THREE.Vector3(point.x, point.y+i, point.z)
  //   }));
  // }
  return bottomPointsAll;
}



/** Get rectangle base points
 * @param {Vector3} point
 * @returns {Vector3[]} bottom points
 */
export function getBottom2HolePoints(bottomCenter) {
  // bottom curve
  const bottomWidth = 80;
  var filletBottom = 5;
  const divFillet = 5;

  var bottomPoints = []
  const centerFillet1 = new THREE.Vector2(bottomWidth/2-filletBottom, -filletBottom);
  const centerFillet2 = new THREE.Vector2(-bottomWidth/2+filletBottom, -filletBottom);
  const centerFillet3 = new THREE.Vector2(-bottomWidth/2+filletBottom, -bottomWidth/2+filletBottom);
  const centerFillet4 = new THREE.Vector2(bottomWidth/2-filletBottom, -bottomWidth/2+filletBottom);

  const fillet1 = new THREE.EllipseCurve(
    centerFillet1.x, centerFillet1.y,
    filletBottom, filletBottom,
    0, 0.5 * Math.PI,
    false, 0
  );
  const fillet2 = new THREE.EllipseCurve(
    centerFillet2.x, centerFillet2.y,
    filletBottom, filletBottom,
    0.5 * Math.PI, Math.PI,
    false, 0
  );
  const fillet3 = new THREE.EllipseCurve(
    centerFillet3.x, centerFillet3.y,
    filletBottom, filletBottom,
    Math.PI, 1.5 * Math.PI,
    false, 0
  );
  const fillet4 = new THREE.EllipseCurve(
    centerFillet4.x, centerFillet4.y,
    filletBottom, filletBottom,
    1.5 * Math.PI, 2 * Math.PI,
    false, 0
  );

  const points1 = fillet1.getPoints( divFillet );
  const points2 = fillet2.getPoints( divFillet );
  const points3 = fillet3.getPoints( divFillet );
  const points4 = fillet4.getPoints( divFillet );

  bottomPoints = bottomPoints.concat(points1);
  bottomPoints = bottomPoints.concat(points2);
  bottomPoints = bottomPoints.concat(points3);
  bottomPoints = bottomPoints.concat(points4);
  bottomPoints.push(bottomPoints[0]);

  bottomPoints = bottomPoints.map((point) => {
     return new THREE.Vector3(point.x+bottomCenter.x, bottomCenter.y, point.y+bottomCenter.z)
  });

  return bottomPoints;
}




/** Get rectangle base points
 * @param {Vector3} point
 * @param {number} bottomWidth
 * @param {number} filletBottom
 * 
 * @returns {Vector3[]} bottom points
 */
export function getBottomPoints(bottomCenter, bottomWidth=80-5, filletBottom=20) {
  // bottom curve
  const divFillet = 5;

  var bottomPoints = []
  const centerFillet1 = new THREE.Vector2(bottomWidth/2-filletBottom, bottomWidth/2-filletBottom);
  const centerFillet2 = new THREE.Vector2(-bottomWidth/2+filletBottom, bottomWidth/2-filletBottom);
  const centerFillet3 = new THREE.Vector2(-bottomWidth/2+filletBottom, -bottomWidth/2+filletBottom);
  const centerFillet4 = new THREE.Vector2(bottomWidth/2-filletBottom,  -bottomWidth/2+filletBottom);

  const fillet1 = new THREE.EllipseCurve(
    centerFillet1.x, centerFillet1.y,
    filletBottom, filletBottom,
    0, 0.5 * Math.PI,
    false, 0
  );
  const fillet2 = new THREE.EllipseCurve(
    centerFillet2.x, centerFillet2.y,
    filletBottom, filletBottom,
    0.5 * Math.PI, Math.PI,
    false, 0
  );
  const fillet3 = new THREE.EllipseCurve(
    centerFillet3.x, centerFillet3.y,
    filletBottom, filletBottom,
    Math.PI, 1.5 * Math.PI,
    false, 0
  );
  const fillet4 = new THREE.EllipseCurve(
    centerFillet4.x, centerFillet4.y,
    filletBottom, filletBottom,
    1.5 * Math.PI, 2 * Math.PI,
    false, 0
  );

  const points1 = fillet1.getPoints( divFillet );
  const points2 = fillet2.getPoints( divFillet );
  const points3 = fillet3.getPoints( divFillet );
  const points4 = fillet4.getPoints( divFillet );

  bottomPoints = bottomPoints.concat(points1);
  bottomPoints = bottomPoints.concat(points2);
  bottomPoints = bottomPoints.concat(points3);
  bottomPoints = bottomPoints.concat(points4);
  bottomPoints.push(bottomPoints[0]);

  bottomPoints = bottomPoints.map((point) => {
     return new THREE.Vector3(point.x+bottomCenter.x, bottomCenter.y, point.y+bottomCenter.z)
  });

  let nPoints = bottomPoints.length;
  bottomPoints = new THREE.CatmullRomCurve3(bottomPoints).getSpacedPoints(nPoints);

  return bottomPoints;
}


/**
 * @param {Vector3} pointA 
 * @param {Vector3} pointB 
 * @param {number} t 
 * @returns {Vector3} lerped point
 */
function lerpPoints(pointA, pointB, t) {
  const x = pointA.x + (pointB.x - pointA.x) * (1-t);
  const z = pointA.z + (pointB.z - pointA.z) * (1-t);
  return new THREE.Vector3(x, 0, z);
}

/**
 * @param {[Vector3]} curve1 base curve1 
 * @param {[Vector3]} curve2 base curve2
 * @param {number} factor tween factor from 0 to 1
 * @returns {[Vector3]} tween curve points
 */
export function calculateTweenCurve(curve1, curve2, factor) {
  if (curve1.length !== curve2.length) {
    throw new Error('Curves must have the same number of points');
  }

  const tweenPoints = [];
  for (let i = 0; i < curve1.length; i++) {
    const pointA = curve1[i];
    const pointB = curve2[i];
    const tweenPoint = lerpPoints(pointA, pointB, factor);
    tweenPoints.push(tweenPoint);
  }

  /*
  // Create the tween curve
  const tweenCurve = new THREE.CatmullRomCurve3(
    tweenPoints.map((point) => new THREE.Vector3(point.x, point.y, point.z))
  );
  
  return tweenCurve;
  */
  return tweenPoints;
}

/** Get center of points 
 * @param {THREE.Vector3[]} points 
 * @returns {THREE.Vector3} center point 
 */
export function calcCentroid(points) {
  const centroid = new THREE.Vector3();
  for (const point of points) {
    centroid.add(point);
  }
  centroid.divideScalar(points.length);
  return centroid;
}

/** Get center of closed curve 
 * @param {THREE.CatmullRomCurve3} curve 
 * @returns {THREE.Vector3} center point 
 */
export function calculateCurveCenter(curve) {
  const points = curve.points;
  const center = new THREE.Vector3();
  
  for (const point of points) {
    center.add(point);
  }
  
  center.divideScalar(points.length);
  return center;
}

export function calculateCurveLength(curve, numPoints = 100) {
  const points = curve.getPoints(numPoints);
  let length = 0;

  for (let i = 0; i < points.length - 1; i++) {
    length += points[i].distanceTo(points[i + 1]);
  }

  return length;
}


/** Get offset curve points 
 * @param {THREE.CatmullRomCurve3} curve 
 * @param {number} scaleFactor, 1.0 is original size
 * @returns {THREE.Vector3[]} intersection points 
 */
export function offsetCurve(curve, scaleFactor, center=null) {
  if (center == null) {
    center = calculateCurveCenter(curve);
  }

  const scaledPoints = curve.points.map((point) => {
    const direction = new THREE.Vector3().subVectors(point, center).normalize();
    const distance = center.distanceTo(point);
    const newDistance = distance * scaleFactor;
    const newPosition = direction.multiplyScalar(newDistance).add(center);

    return newPosition;
  });

  return scaledPoints;
}

export function offsetCurveMm(curve, mm, center=null) {
  if (center == null) {
    center = calculateCurveCenter(curve);
  }

  const scaledPoints = curve.points.map((point) => {
    const direction = new THREE.Vector3().subVectors(point, center).normalize();
    const distance = center.distanceTo(point);
    const newDistance = distance * (1 + mm/distance);
    const newPosition = direction.multiplyScalar(newDistance).add(center);

    return newPosition;
  });


  return scaledPoints;
}

export function intersectionPointsPlane(points, planeVec, planeDistance=0) {
  const plane = new THREE.Plane(planeVec, planeDistance);
  const intersections = [];

  if (points[0] instanceof THREE.Vector3 === false) {
    points = points.map((point) => new THREE.Vector3(point.x, point.y, point.z));
  }

  // 各線分と平面との交点を計算
  for (let i = 0; i < points.length-1; i++) {
    const startPoint = points[i];
    const endPoint = points[i+1];

    const line = new THREE.Line3(startPoint, endPoint);
    const intersection = new THREE.Vector3();

    if (plane.intersectLine(line, intersection)) {
      intersections.push(intersection);
    }
  }

  return intersections;
}

/** Get intersection points between curve and plane
 * @param {THREE.CatmullRomCurve3} curve 
 * @param {THREE.Plane} plane
 * @returns {THREE.Vector3[]} intersection points 
 */
export function intersectionCurvePlane(curve, plane, numPoints=100, close=true) {
  const points = curve.getPoints(numPoints);
  const intersections = [];

  for (let i = 0; i < numPoints; i++) {
    if ( close == false && i == numPoints - 1) { break }

    const segmentStart = points[i];
    const segmentEnd = points[(i + 1) % numPoints];
    const line = new THREE.Line3(segmentStart, segmentEnd);

    const intersection = new THREE.Vector3();

    if (plane.intersectLine(line, intersection)) {
      //console.log(line)
      //console.log(intersection)
      intersections.push(intersection);
    }
  }

  return intersections;
}


/**
 * @param {THREE.CatmullRomCurve3} curve 
 * @param {number} angleStep 
 * @param {boolean} close
 * @param {THREE.Vector3} centerPoint
 * @returns {[Vector3]} 
 */
export function calculateIntersectionPoints(curve, angleStep, close=false, centerPoint=false) {
  const center = centerPoint ? centerPoint : calculateCurveCenter(curve);
  
  const intersections = [];
  var intersectionPoint = new THREE.Vector3();

  for (let angle = 0; angle < 360; angle += angleStep) {
    const radians = THREE.MathUtils.degToRad(angle);
    const normal = new THREE.Vector3(Math.cos(radians), 0, Math.sin(radians));
    const plane = new THREE.Plane(normal, -normal.dot(center));

    const intersectionPoints = intersectionCurvePlane(curve, plane, 300);

    var x0 = intersectionPoints[0].x - center.x;
    var z0 = intersectionPoints[0].z - center.z;
    var x1 = intersectionPoints[1].x - center.x;
    var z1 = intersectionPoints[1].z - center.z;
    var temp = 0;

    const minXIdx = intersectionPoints.reduce((minIdx, vector, index) => {
      if (vector.x < intersectionPoints[minIdx].x) {
        return index;
      }
      return minIdx;
    }, 0);

    const maxXIdx = intersectionPoints.reduce((maxIdx, vector, index) => {
      if (vector.x > intersectionPoints[maxIdx].x) {
        return index;
      }
      return maxIdx;
    }, 0);

    const minZIdx = intersectionPoints.reduce((minIdx, vector, index) => {
      if (vector.z < intersectionPoints[minIdx].z) {
        return index;
      }
      return minIdx;
    }, 0);

    const maxZIdx = intersectionPoints.reduce((maxIdx, vector, index) => {
      if (vector.z > intersectionPoints[maxIdx].z) {
        return index;
      }
      return maxIdx;
    }, 0);
    
    
    if ( angle === 0 ) {
      intersectionPoint = intersectionPoints[maxZIdx];
    } else if (angle === 90) {
      intersectionPoint = intersectionPoints[minXIdx];
    } else if (angle === 180) {
      intersectionPoint = intersectionPoints[minZIdx];
    } else if (angle === 270) {
      intersectionPoint = intersectionPoints[maxXIdx];
    } else {
      if (x0 > 0 && z0 > 0) {
        temp = 1;
      } else if (x0 < 0 && z0 > 0) {
        temp = 2;
      } else if (x0 < 0 && z0 < 0) {
        temp = 3;
      } else if (x0 > 0 && z0 < 0) {
        temp = 4;
      }

      if (angle > 0 && angle < 90) {
        (temp === 2) ? intersectionPoint = intersectionPoints[0] : intersectionPoint = intersectionPoints[1];
      } else if (angle > 90 && angle < 180) {
        (temp === 3) ? intersectionPoint = intersectionPoints[0] : intersectionPoint = intersectionPoints[1];
      } else if (angle > 180 && angle < 270) {
        (temp === 4) ? intersectionPoint = intersectionPoints[0] : intersectionPoint = intersectionPoints[1];
      } else if (angle > 270 && angle < 360) {
        (temp === 1) ? intersectionPoint = intersectionPoints[0] : intersectionPoint = intersectionPoints[1];
      }
    }

    intersections.push(intersectionPoint);
  }

  if (close) intersections.push(intersections[0]);

  return intersections;
}


export function lineSegmentsIntersect(a1, a2, b1, b2) {
  const ua_t = (b2.z - b1.z) * (a1.y - b1.y) - (b2.y - b1.y) * (a1.z - b1.z);
  const ub_t = (a2.z - a1.z) * (a1.y - b1.y) - (a2.y - a1.y) * (a1.z - b1.z);
  const u_b = (b2.y - b1.y) * (a2.z - a1.z) - (b2.z - b1.z) * (a2.y - a1.y);

  if (u_b !== 0) {
    const ua = ua_t / u_b;
    const ub = ub_t / u_b;

    if (0 <= ua && ua <= 1 && 0 <= ub && ub <= 1) {
      return new THREE.Vector3(
        a1.x + ua * (a2.x - a1.x),
        a1.y + ua * (a2.y - a1.y),
        a1.z + ua * (a2.z - a1.z)
      );
    }
  }

  return null;
}

export function findClosestToZeroY(arr, minus=true) {
  let closest = null;
  let closestDistance = Infinity;

  for (let i = 0; i < arr.length; i++) {
    const vector = arr[i];

    if (minus) {
      if (vector.z < 0) {
        const distanceToZero = Math.abs(vector.y);
        
        if (distanceToZero < closestDistance) {
          closest = vector;
          closestDistance = distanceToZero;
        }
      }
    } else {
      if (vector.z >= 0) {
        const distanceToZero = Math.abs(vector.y);
        
        if (distanceToZero < closestDistance) {
          closest = vector;
          closestDistance = distanceToZero;
        }
      }
    }
  }

  return closest;
}

export function calcIntersectionMedialCurves(curve, trimCurve, divisions=100) {
  const curvePoints = curve.getPoints(divisions);
  const trimCurvePoints = trimCurve.getPoints(divisions);
  const intersections = [];

  const zeroPointMinus = findClosestToZeroY(trimCurvePoints);
  const zeroPointPlus = findClosestToZeroY(trimCurvePoints, false);

  return [zeroPointMinus, zeroPointPlus];
}

export function shatterCurvePoints(crossPoints, idx0, idx1) {
  const shatterPointsAll = [];

  if (idx0 > idx1) {
    const tmp = idx0;
    idx0 = idx1;
    idx1 = tmp;
  }

  shatterPointsAll.push(crossPoints.slice(idx0, idx1+1));
  shatterPointsAll.push(crossPoints.slice(idx1+1, crossPoints.length).concat(crossPoints.slice(0, idx0)));

  if (shatterPointsAll[0].length > shatterPointsAll[1].length) {
    shatterPointsAll.reverse();
  }

  return shatterPointsAll;
}

/**
 * Converts an array of points to an array of Vector3 objects.
 *
 * @param {Array} arr - The array of points to convert.
 * @returns {Array} An array of Vector3 objects.
 */
export function convertArrayToVector3Array(arr) {
  return arr.map((point) => new THREE.Vector3(point.x, point.y, point.z));
}


/**
 * Find closest point on points
 * @param {THREE.Vector3[]} points, source points
 * @param {THREE.Vector3} point, target points
 * @returns {THREE.Vector3[]} closest point and index
 */
export function findClosestPointOnPoints(points, targetPoint) {
  const closestPoint = new THREE.Vector3();
  let closestDistance = Infinity;
  let closestIdx = 0;

  // convert to Vector3
  if (targetPoint instanceof THREE.Vector3 === false) {
    targetPoint = new THREE.Vector3(targetPoint.x, targetPoint.y, targetPoint.z);
  }

  // find closest point
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    const distance = targetPoint.distanceTo(point); // calculate distance between target point and point
    
    if (distance < closestDistance) {
      closestDistance = distance;
      closestPoint.copy(point); // update closest point
      closestIdx = i;
    }
  }

  return {
    point: closestPoint,
    idx: closestIdx
  };
}

export function remapCurve(curve, distanceBetweenPoints) {
  const curveLength = curve.getLength();
  const divisions = Math.round(curveLength / distanceBetweenPoints);
  const points = [];

  for (let i = 0; i <= divisions; i++) {
    const t = i / divisions;
    const point = curve.getPoint(t);
    points.push(point);
  }

  return points;
}

/**
 * Align seam points
 * @param {THREE.Vector3[]} points1, this is seam points, but we will use only first point
 * @param {THREE.Vector3[]} points2, target points
 * @returns {THREE.Vector3[]} alignedPoints
 */
export function alignSeamPoints(points1, points2) {
  let minDistance = Infinity;
  let minIndex = 0;

  for (let i = 0; i < points2.length; i++) {
    const distance = points1[0].distanceTo(points2[i]);
    if (distance < minDistance) {
      minDistance = distance;
      minIndex = i;
    }
  }

  if (minIndex > 0) {
    const alignedPoints2 = [...points2.slice(minIndex), ...points2.slice(0, minIndex)];
    return alignedPoints2;
  }

  return points2;
}

/**
 * Align seam points
 * @param {THREE.Vector3[]} points1, this is seam points, but we will use only first point
 * @param {THREE.Vector3[]} points2, target points
 * @returns {THREE.Vector3[]} alignedPoints
 */
export function alignSeamPointsFrom3Dto2D(points1, points2) {
  let minDistance = Infinity;
  let minIndex = 0;

  const refPoint = new THREE.Vector3(points1[0].x, 0, points1[0].z);
  for (let i = 0; i < points2.length; i++) {
    const targetPoint = new THREE.Vector3(points2[i].x, 0, points2[i].z);
    const distance = refPoint.distanceTo(targetPoint);
    if (distance < minDistance) {
      minDistance = distance;
      minIndex = i;
    }
  }

  if (minIndex > 0) {
    const alignedPoints2 = [...points2.slice(minIndex), ...points2.slice(0, minIndex)];
    return alignedPoints2;
  }


  return points2;
}


/**
 * Makes the given curve clockwise by reversing the order of points if the area is negative.
 * @param {Array<THREE.Vector3>} points - The points of the curve.
 * @returns {Array<THREE.Vector3>} The clockwise curve points.
 */
export function makeCurveClockwise(points) {
  const n = points.length;
  let area = 0;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += (points[j].x - points[i].x) * (points[j].z + points[i].z);
  }

  if (area > 0) {
    return points.reverse();
  }

  return points;
}

export function createLoftSurfaceFrom2Curves(curve1, curve2, segmentsWidth, segmentsHeight, color='white', side=THREE.FrontSide) {
  var cnt = 0;
  const parametricFunction = (u, v, target) => {
    const pointOnCurve1 = curve1.getPoint(u);
    const pointOnCurve2 = curve2.getPoint(u);
    const interpolationVector = new THREE.Vector3().subVectors(pointOnCurve2, pointOnCurve1);

    const x = pointOnCurve1.x + interpolationVector.x * v;
    const y = pointOnCurve1.y + interpolationVector.y * v;
    const z = pointOnCurve1.z + interpolationVector.z * v;

    target.set(x, y, z);

    cnt += 1;
  };

  const geometry = new ParametricGeometry(
    parametricFunction,
    segmentsWidth,
    segmentsHeight
  );
  //const material = new THREE.MeshBasicMaterial({ side: side, color: color, transparent: true, opacity: 0.4 });
  //const material = new THREE.MeshLambertMaterial({color: color, transparent: true, opacity: 0.6, side: THREE.DoubleSide});
  const material = new THREE.MeshMatcapMaterial({color: color, transparent: true, opacity: 0.6, side: THREE.DoubleSide});
  const mesh = new THREE.Mesh(geometry, material);

  return mesh;
}

export function createLoftSurfaceFrom2CurvesSmooth(curve1, curve2, segmentsWidth, segmentsHeight, color='white', side=THREE.FrontSide) {
  const parametricFunction = (u, v, target) => {
    const pointsOnCurve1 = curve1.getPoints(segmentsWidth);
    const pointsOnCurve2 = curve2.getPoints(segmentsWidth);
    
    const splines = pointsOnCurve1.map((point, i) => {
        return new THREE.CatmullRomCurve3([point, pointsOnCurve2[i]]);
    });
    
    const pointOnSpline = splines[Math.floor(u * (splines.length-1))].getPoint(v);
    
    target.set(pointOnSpline.x, pointOnSpline.y, pointOnSpline.z);
  };

  const geometry = new ParametricGeometry(
    parametricFunction,
    segmentsWidth,
    segmentsHeight
  );
  //const material = new THREE.MeshBasicMaterial({ side: side, color: color, transparent: true, opacity: 0.4 });
  const material = new THREE.MeshLambertMaterial({color: color, transparent: true, opacity: 0.6, side: THREE.DoubleSide});
  console.log(material)
  const mesh = new THREE.Mesh(geometry, material);

  return mesh;
}

export function createLoftSurfaceFromCurves(weftPoints, segmentsWidth, segmentsHeight, color='white', close=false) {

  const curves = weftPoints.map((points) => {
    return new THREE.CatmullRomCurve3(points, close);
  });

  const parametricFunction = (u, v, target) => {
    // 各 v 値での点の配列を生成
    const points = curves.map((curve) => {
      //return curve.getPoint(u);
      return curve.getPointAt(u);
    });

    // これらの点を通る Catmull-Rom スプライン曲線を作成
    const warpCurve = new THREE.CatmullRomCurve3(points);
    
    // このスプラインから u 値に対応する点を取得
    //const point = warpCurve.getPoint(v);
    const point = warpCurve.getPointAt(v);

    target.set(point.x, point.y, point.z);
  };

  const geometry = new ParametricGeometry(
    parametricFunction,
    segmentsWidth,
    segmentsHeight
  );
  //const material = new THREE.MeshBasicMaterial({ side: side, color: color, transparent: true, opacity: 0.4 });
  //const material = new THREE.MeshLambertMaterial({color: color, transparent: true, opacity: 0.6, side: THREE.DoubleSide});
  const material = new THREE.MeshMatcapMaterial({color: color, transparent: true, opacity: 0.6, side: THREE.DoubleSide});
  const mesh = new THREE.Mesh(geometry, material);

  return mesh;
}

/*
export function createLoftSurfaceFrom2CurvesSmooth(curve1, curve2, curveWarpPointsAll, segmentsWidth, segmentsHeight, color='white', side=THREE.FrontSide) {
  var uIdx = 0;
  var vIdx = -1;
  var cnt = 0;
  const parametricFunction = (u, v, target) => {
    if (cnt % segmentsWidth === 0) {
      uIdx = 0;
      vIdx += 1;
    }
    const curveWarpPoints = curveWarpPointsAll[uIdx];

    // const pointOnCurve1 = curve1.getPoint(u);
    // const pointOnCurve2 = curve2.getPoint(u);
    // const interpolationVector = new THREE.Vector3().subVectors(pointOnCurve2, pointOnCurve1);

    // const x = pointOnCurve1.x + interpolationVector.x * v;
    // const y = pointOnCurve1.y + interpolationVector.y * v;
    // const z = pointOnCurve1.z + interpolationVector.z * v;

    // target.set(x, y, z);

    
    console.log(uIdx, u, v)

    target.set(curveWarpPoints[vIdx].x, curveWarpPoints[vIdx].y, curveWarpPoints[vIdx].z)



    uIdx += 1;
    cnt += 1;
  };

  const geometry = new ParametricGeometry(
    parametricFunction,
    segmentsWidth,
    segmentsHeight
  );
  //const material = new THREE.MeshBasicMaterial({ side: side, color: color, transparent: true, opacity: 0.4 });
  const material = new THREE.MeshLambertMaterial({color: color, transparent: true, opacity: 0.6, side: THREE.DoubleSide});
  const mesh = new THREE.Mesh(geometry, material);

  return mesh;
}
*/

export function findIntersectionPoints(bufferGeometry, plane) {
  const vertices = bufferGeometry.getAttribute("position").array;
  const indices = bufferGeometry.getIndex().array;

  const intersectionPoints = [];

  for (let i = 0; i < indices.length; i += 3) {
    const a = new THREE.Vector3().fromArray(vertices, indices[i] * 3);
    const b = new THREE.Vector3().fromArray(vertices, indices[i + 1] * 3);
    const c = new THREE.Vector3().fromArray(vertices, indices[i + 2] * 3);

    const da = plane.distanceToPoint(a);
    const db = plane.distanceToPoint(b);
    const dc = plane.distanceToPoint(c);

    if (da * db < 0) {
      const intersectionPoint = a.clone().lerp(b, da / (da - db));
      intersectionPoints.push(intersectionPoint);
    }
    if (db * dc < 0) {
      const intersectionPoint = b.clone().lerp(c, db / (db - dc));
      intersectionPoints.push(intersectionPoint);
    }
    if (dc * da < 0) {
      const intersectionPoint = c.clone().lerp(a, dc / (dc - da));
      intersectionPoints.push(intersectionPoint);
    }
  }

  return intersectionPoints;
}

export function _sortPoints2(points) {
  // 距離オブジェクトの配列
  const distances = [];

  // 距離オブジェクトを作成
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const distance = points[i].distanceTo(points[j]);
      distances.push({
        point1: points[i],
        point2: points[j],
        distance: distance
      });
    }
  }

  // 距離に基づいて距離オブジェクトを並べ替え
  distances.sort((a, b) => a.distance - b.distance);

  // 並べ替えたオブジェクトからVector3の配列を作成
  const sortedPoints = [];
  distances.forEach((item) => {
    sortedPoints.push(item.point1);
    sortedPoints.push(item.point2);
  });

  return sortedPoints
}

function findClosestPointIndex(point, points) {
  let closestIndex = -1;
  let closestDistance = Infinity;

  points.forEach((otherPoint, index) => {
    const distance = point.distanceTo(otherPoint);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
}

export function sortPoints2(points) {
  const sortedPoints = [];
  const remainingPoints = points.slice();

  let currentPoint = remainingPoints.shift();
  sortedPoints.push(currentPoint);

  while (remainingPoints.length > 0) {
    const closestIndex = findClosestPointIndex(currentPoint, remainingPoints);
    currentPoint = remainingPoints.splice(closestIndex, 1)[0];
    sortedPoints.push(currentPoint);
  }

  return sortedPoints;
}


/**
 * Sorts an array of points based on their distance to each other.
 * @param {Array} points - The array of points to be sorted.
 * @param {number} [distanceThreshold=50] - The maximum distance between two points for them to be considered close.
 * @returns {Array} - The sorted array of points.
 */
export function sortPoints(points, distanceThreshold=50, idx=false) {
  const sortedPointsIdx = [];
  const sortedPoints = [...points]; // coppy array of points
  const numPoints = sortedPoints.length;

  for (let i = 0; i < numPoints-1 ; i++) {
    const currentPoint = sortedPoints[i];
    let closestPoint = null;
    let closestDistance = Infinity;

    // Find the closest point to the current point
    for (let j = i + 1; j < numPoints; j++) {
      const distance = currentPoint.distanceTo(sortedPoints[j]);

      if (distance < closestDistance && distance <= distanceThreshold) {
        closestPoint = sortedPoints[j];
        closestDistance = distance;
      }
    }

    // Find the closest point and move it to the next index
    if (closestPoint) {
      sortedPoints.splice(sortedPoints.indexOf(closestPoint), 1);
      sortedPoints.splice(i + 1, 0, closestPoint);

      sortedPointsIdx.push(points.indexOf(closestPoint));
    }
  }

  if (idx === true) {
    return [sortedPoints, sortedPointsIdx];
  } else {
    return sortedPoints;
  }
}


export const sortPointsWithVector = (points) => {
  const sortedPoints = [...points]; // coppy array of points
  const numPoints = sortedPoints.length;

  for (let i = 1; i < numPoints ; i++) {
    const currentPoint = sortedPoints[i % numPoints];

    // calc vector from previous point to current point
    const vector = new THREE.Vector3().subVectors(currentPoint, sortedPoints[i-1]);

    let closestPoint = null;
    let closestDistance = Infinity;

    // Find the closest point to the current point
    for (let j = i + 1; j < numPoints; j++) {
      const distance = currentPoint.distanceTo(sortedPoints[j]);
      const vectorToNext = new THREE.Vector3().subVectors(sortedPoints[j], currentPoint);
      const innerProduct = vector.dot(vectorToNext);

      // if (distance < closestDistance && vector.angleTo(vectorToNext) < Math.PI/2) {
      if (distance * innerProduct < closestDistance) {
        closestPoint = sortedPoints[j];
        closestDistance = distance;
      }
    }

    // Find the closest point and move it to the next index
    if (closestPoint) {
      sortedPoints.splice(sortedPoints.indexOf(closestPoint), 1);
      sortedPoints.splice(i + 1, 0, closestPoint);
    }
  }

  return sortedPoints;
}


/**
 * Get wrap contour points from surface
 * @param {THREE.Mesh} mesh
 * @param {THREE.Vector3} bottomCenter
 * @param {number} adductAngle
 * @param {number} flexAngle
 * @param {number} angleStep
 * @param {number} yFilter
 * @returns {THREE.Vector3[]} contourPoints
 */
export function getContourPointsCircumAll(surface, bottomCenter, adductAngle, flexAngle, angleStep=20, yFilter=-50, sort=false) {
  const tempArray = [];
  const tempFirst = [];
  const tempSecond = []
  
  for (let angle = 0; angle < 180; angle += angleStep) {
    const center = bottomCenter.clone()
    const radians = THREE.MathUtils.degToRad(angle);
    const normal = new THREE.Vector3(Math.cos(radians), 0, Math.sin(radians));
    // add flexion angle around bottom center to align cutting plane angle
    //normal.applyAxisAngle(bottomCenter, THREE.MathUtils.degToRad(flexAngle));
    const plane = new THREE.Plane(normal, -normal.dot(center));

    var vertices = drawIntersectionPoints(surface, plane, false, false, false)

    var contourPoints = sortPoints(vertices)
    contourPoints = contourPoints.filter(point => point.y >= yFilter);
    if (sort) {
      contourPoints = sortPoints(contourPoints);
    }

    const distances = [];
    for (let i = 0; i < contourPoints.length-1; i++) { // -1は最後の要素（直線Aの最後）と最初の要素（直線Bの最初）を除くため
      const point = contourPoints[i];
      const nextPoint = contourPoints[(i + 1) % contourPoints.length];
      distances.push(point.distanceTo(nextPoint));
    }

    const max = Math.max(...distances);
    var maxIndex = distances.indexOf(max);

    if (maxIndex == distances.length-1) {
      maxIndex = Math.round(distances.length/2);
    }


    var firstHalf = sortPoints(contourPoints.slice(0, maxIndex+1));
    var secondHalf = sortPoints(contourPoints.slice(maxIndex+1, contourPoints.length));


    const refPoint = new THREE.Vector3(Math.sin(radians), 0, Math.cos(radians)).add(normal.multiplyScalar(50));
    if (firstHalf[0].distanceTo(refPoint) > secondHalf[0].distanceTo(refPoint)) {
      // swap
      const temp = firstHalf;
      firstHalf = secondHalf;
      secondHalf = temp;
    }
    
    tempArray.push(firstHalf);
    tempSecond.push(secondHalf)
  }

  const warpPointsAll = tempArray.concat(tempSecond, tempFirst)
  return warpPointsAll;
}

/**
 * Get wrap contour points from surface
 * @param {THREE.Mesh} mesh
 * @param {THREE.Vector3} bottomCenter
 * @param {number} adductAngle
 * @param {number} flexAngle
 * @param {number} angleStep
 * @param {number} yFilter
 * @returns {THREE.Vector3[]} contourPoints
 */
export function getWrapPoints(surface, bottomCenter, adductAngle, flexAngle, angleStep=20, yFilter=-50, sort=false) {
  const tempArray = [];
  const tempFirst = [];
  const tempSecond = [];
  const refPoints = [];
  
  for (let angle = 0; angle < 180; angle += angleStep) {
    const center = bottomCenter.clone()
    const radians = THREE.MathUtils.degToRad(angle);
    const normal = new THREE.Vector3(Math.cos(radians), 0, Math.sin(radians));
    // add flexion angle around bottom center to align cutting plane angle
    //normal.applyAxisAngle(bottomCenter, THREE.MathUtils.degToRad(flexAngle));
    const plane = new THREE.Plane(normal, -normal.dot(center));
    
    // rotate normal vector around origin by 90
    const rotatedNormal = new THREE.Vector3(-normal.z, 0, normal.x);
    let refPoint = bottomCenter.clone().add(rotatedNormal.clone().multiplyScalar(50));
    let refPoint2 = bottomCenter.clone().add(rotatedNormal.clone().multiplyScalar(-50));
    refPoints.push(refPoint);

    var vertices = drawIntersectionPoints(surface, plane, false, false, false)
    vertices = vertices.filter(point => point.y >= yFilter);

    // find the max y vertex from the vertices and move it to the first index
    var maxY = Math.max(...vertices.map((point) => point.y));
    var maxIndex = vertices.findIndex((point) => point.y === maxY);
    vertices = vertices.slice(maxIndex, vertices.length).concat(vertices.slice(0, maxIndex));

    let firstHalf = [];
    let secondHalf = [];

    vertices.map((point) => {
      refPoint.y = point.y;
      refPoint2.y = point.y;

      if (refPoint.distanceTo(point) > refPoint2.distanceTo(point)) {
        firstHalf.push(point);
      } else {
        secondHalf.push(point);
      }
    })

    firstHalf = sortPoints(firstHalf);
    secondHalf = sortPoints(secondHalf);

    // sort points with y value
    firstHalf = firstHalf.sort((a, b) => a.y - b.y);
    secondHalf = secondHalf.sort((a, b) => a.y - b.y);


    if (firstHalf[0].y > firstHalf[firstHalf.length-1].y) {
      firstHalf.reverse();
    }
    if (secondHalf[0].y > secondHalf[secondHalf.length-1].y) {
      secondHalf.reverse();
    }

    // const refPoint = new THREE.Vector3(Math.sin(radians), 0, Math.cos(radians)).add(normal.multiplyScalar(50));
    if (firstHalf[0].distanceTo(refPoint) > secondHalf[0].distanceTo(refPoint)) {
      // swap
      const temp = firstHalf;
      firstHalf = secondHalf;
      secondHalf = temp;
    }
    
    tempArray.push(firstHalf);
    tempSecond.push(secondHalf)
  }

  const warpPointsAll = tempArray.concat(tempSecond, tempFirst)
  
  return [warpPointsAll, refPoints];
}

export function offsetPointsNormalBufferGeometry(geometry, points, offsetDistance, center=null) {
  geometry.computeVertexNormals();
  
  const vertices = geometry.getAttribute('position').array;
  const normals = geometry.getAttribute('normal').array;

  points.forEach((point) => {
    const nearestIndex = findNearestVertex(point, vertices);
    const normal = new THREE.Vector3(
      normals[nearestIndex * 3],
      normals[nearestIndex * 3 + 1],
      normals[nearestIndex * 3 + 2]
    );
    
    if (center == null) {
      point.add(normal.multiplyScalar(offsetDistance));
    } else {
      var tempPoint = point.clone();
      tempPoint.add(normal.multiplyScalar(offsetDistance));

      if ((tempPoint.distanceTo(center)) > (point.distanceTo(center))) {
        point.sub(normal.multiplyScalar(offsetDistance));
      } else {
        point.add(normal.multiplyScalar(offsetDistance));
      }
    }
  });

  return points
}

export function findNearestVertex(point, vertices) {
  let minDistance = Infinity;
  let nearestIndex = -1;

  for (let i = 0; i < vertices.length; i += 3) {
    const vertex = new THREE.Vector3(vertices[i], vertices[i + 1], vertices[i + 2]);
    const distance = point.distanceToSquared(vertex);

    if (distance < minDistance) {
      minDistance = distance;
      nearestIndex = i;
    }
  }

  return nearestIndex / 3;
}



export function calcWrapPointsLowerUpperSurface(lowerSurfaceMesh, upperSurfaceMesh, bottomCenter, centroidBaseshape, adductAngle, flexAngle, angleStep=10) {
  // get warp points to merge lower and upper surface ///////////////////////////////////////////
  var lowerWarpPointsAll = getContourPointsCircumAll(lowerSurfaceMesh, bottomCenter, adductAngle, flexAngle, angleStep, -10000);
  var upperWarpPointsAll = getContourPointsCircumAll(upperSurfaceMesh, bottomCenter, adductAngle, flexAngle, angleStep, -10000);

  // delete abonormal bottom points for lower surface
  lowerWarpPointsAll = lowerWarpPointsAll.map((points, idx) => {
    // calculate average distance between points and if distance is too large, delete the point
    const distances = [];
    const threshold = 10; // raius of bottom curve
    const offsetY = 30;
    for (let i = 0; i < points.length-1; i++) { // delete last point
      const point = points[i];
      const nextPoint = points[(i + 1) % points.length];
      distances.push(point.distanceTo(nextPoint));
    }
    const averageDistance = distances.reduce((sum, value) => sum + value) / distances.length;
    // find max distance index and delete the point from the index
    const max = Math.max(...distances);
    var maxIndex = distances.indexOf(max);
    
    // if max distance is too large, delete the point
    if (max > threshold) {
      if (points[maxIndex].y < offsetY + bottomCenter.y) { // delete around bottom center
        // delete points from index 0 to maxIndex
        return points.slice(maxIndex+1, points.length);
      }
    }
    return points;
  })



  // delete abonormal points for upper surface
  upperWarpPointsAll = upperWarpPointsAll.map((points) => {
    // sort by Y if maxY > 20 (lateral wall mesh is strange), TODO: this is not smart way
    const maxY = Math.max(...points.map((point) => point.y));
    if (maxY > 30) {
      // sort by Y
      points.sort((a, b) => {
        return a.y - b.y;
      })
    } else {
      points.sort((a, b) => {
        return a.distanceTo(centroidBaseshape) - b.distanceTo(centroidBaseshape);
      })
    }

    if (points[0].distanceTo(centroidBaseshape) > points[points.length-1].distanceTo(centroidBaseshape)) {
      points.reverse();
    }

    return points;
    //return sortByMaxDistanceFromCenter(points, centroidBaseshape);
  })

  return [lowerWarpPointsAll, upperWarpPointsAll];
}

export function mergeLowerUpperPoints(lowerWarpPointsAll, upperWarpPointsAll, div) {
  var wrapPointsAll = [];
  for (let i=0; i<lowerWarpPointsAll.length; i++) {
    wrapPointsAll.push(lowerWarpPointsAll[i].concat(upperWarpPointsAll[i]))
  }
  //wrapPointsAll.push(wrapPointsAll[0])

  var divWarpPointsAll = [];
  divWarpPointsAll = wrapPointsAll.map((points) => {
    const temp = new THREE.CatmullRomCurve3(points);
    return temp.getSpacedPoints(div);
  });
  return divWarpPointsAll;
}


/**
 * Get surface buffergeometry from points
 * @param {THREE.Vector3[]} points
 * @returns {THREE.BufferGeometry} buffergeometry
 */
export function createBottmCapSurface(points) {
  const centroid = new THREE.Vector3();
  points.forEach((vector) => {
    centroid.add(vector);
  });
  centroid.divideScalar(points.length);
  points.unshift(centroid);

  
  const bottomGeometry = new THREE.BufferGeometry();
  const loftPoints0Raw = []
  points.map((point) => loftPoints0Raw.push(point.x, point.y, point.z));
  bottomGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(loftPoints0Raw), 3));
  const indices = [];

  for (let i = 1; i < points.length-1; i++) {
    indices.push(0, i, i + 1);
  }
  indices.push(0, points.length-1, 1); // 最後の三角形を追加
  bottomGeometry.setIndex(indices);
  bottomGeometry.computeVertexNormals();
  // 空のuv属性を追加, 後で追加するparametric geometryと同じ属性を持つ必要がある
  const vertexCount = bottomGeometry.getAttribute('position').count;
  const emptyUVs = new Float32Array(vertexCount * 2); // 各頂点について2つの要素（u、v）が必要
  const uvAttribute = new THREE.BufferAttribute(emptyUVs, 2);
  bottomGeometry.setAttribute('uv', uvAttribute);

  return bottomGeometry;
}


/**
 * Calculates the middle points on a spline curve based on the given control points.
 * @param {Array<THREE.Vector3>} points - The control points of the spline curve.
 * @returns {Array<THREE.Vector3>} - The middle points on the spline curve.
 */
export const getMiddlePointsOnSpline = (points, close=false, divPoints=200) => {
  // deepcopy points
  const newPoints = points.map((point) => new THREE.Vector3(point.x, point.y, point.z));

  const curve = new THREE.CatmullRomCurve3(newPoints, close, "catmullrom");
  const curvePoints = curve.getSpacedPoints(divPoints);

  if (close) {
    newPoints.push(newPoints[0]);
  }

  // find closest point on curve for each point
  const cPoints = [];
  let firstIdx = 0;
  for (let i = 1; i < newPoints.length; i++) {
    // find closest point on curve
    let minDist = 99999;
    let minIdx = -1;

    if (close && i == newPoints.length-1) { // last point treatment for close curve
      cPoints.push(curvePoints[Math.floor(firstIdx + (curvePoints.length - firstIdx) / 2)]);
    } else {
      for (let j = 0; j < curvePoints.length; j++) {
        const dist = newPoints[i].distanceTo(curvePoints[j]);
        if (dist < minDist) {
          minIdx = j;
          minDist = dist;
        }
      }
      // get middle index between first and minIdx
      const midIdx = Math.floor((firstIdx + minIdx) / 2);
      cPoints.push(curvePoints[midIdx]);

      // update firstIdx
      firstIdx = minIdx;
    }
  }
  
  return cPoints;
}
